import {useCallback, useMemo} from 'react';
import {
  AverageRatingType,
  CountryOfOrigin,
  InputOptionWithIconType,
} from '../types/index';
import {ValueOf} from 'type-fest';
import {Metafield} from '@shopify/hydrogen/storefront-api-types';
import {PartialDeep} from 'type-fest/source/partial-deep';
import {parseMetafieldValue} from '@shopify/hydrogen';
import {
  ShopifyProductMetafieldKeyValueType,
  LatestEventValueType,
} from '../shopifyGraphql/products';

export interface UseShopifyProductMetafieldsType {
  metafieldsKeyValue: Readonly<ShopifyProductMetafieldKeyValueType>;
}

/*
  Helper utility for resolving Shopify Product metafields into usable format for UI components.
 */
export default function useShopifyProductMetafields(
  metafields: PartialDeep<Metafield>[],
): UseShopifyProductMetafieldsType {
  const getMetafieldValue = useCallback(
    <T extends ValueOf<UseShopifyProductMetafieldsType['metafieldsKeyValue']>>(
      fieldKey: keyof ShopifyProductMetafieldKeyValueType,
    ): T | null => {
      const metafield =
        metafields &&
        metafields.find((metafield) => metafield?.key === fieldKey);
      const value = metafield && parseMetafieldValue(metafield);

      let resolvedValue = value;
      switch (fieldKey) {
        case 'country': {
          if (resolvedValue) {
            resolvedValue = Object.values(CountryOfOrigin).find(
              ({label}) => label === value,
            );
            if (!resolvedValue) {
              resolvedValue = {
                ...CountryOfOrigin.restOfTheWorld,
                label: value as string,
              };
            }
          }
          break;
        }
      }
      return resolvedValue as T;
    },
    [metafields],
  );

  const metafieldsKeyValue = useMemo(
    () =>
      Object.freeze({
        rewardsMembersOnly: getMetafieldValue<boolean>('rewardsMembersOnly'),
        abv: getMetafieldValue<number>('abv'),
        age: getMetafieldValue<number>('age'),
        averageRating: getMetafieldValue<AverageRatingType>('averageRating'),
        brand: getMetafieldValue<string>('brand') as string,
        buyerSavingAmount: getMetafieldValue<number>('buyerSavingAmount'),
        cask: getMetafieldValue<string>('cask'),
        country: getMetafieldValue<InputOptionWithIconType>('country'),
        distillery: getMetafieldValue<string>('distillery'),
        filter: getMetafieldValue<string>('filter'),
        latestEvent: getMetafieldValue<LatestEventValueType>('latestEvent'),
        peatLevel: getMetafieldValue<string>('peatLevel'),
        region: getMetafieldValue<string>('region'),
        series: getMetafieldValue<string>('series'),
        sponsoredProductVariant: getMetafieldValue<string>(
          'sponsoredProductVariant',
        ),
        style: getMetafieldValue<string>('style'),
        summary: getMetafieldValue<string>('summary'),
        totalReviews: getMetafieldValue<number>('totalReviews'),
        type: getMetafieldValue<string>('type'),
        uuid: getMetafieldValue<string>('uuid'),
        volumeMl: getMetafieldValue<number>('volumeMl'),
        sampleProduct: getMetafieldValue<string>('sampleProduct'),
        tileTextBadge: getMetafieldValue<string>('tileTextBadge'),
      }),
    [getMetafieldValue],
  );

  return {
    metafieldsKeyValue,
  };
}
